'use client'

import { useEffect, useRef, useState } from 'react'
import { MapPin } from 'lucide-react'
import { REGIONAL_CLIMATE_DATA } from '@/data/regional-climate'
import type { ComputedTotals } from '@/lib/grass-comparison-scoring'
import type { ZipPrefillSource } from '@/hooks/use-compare-zip'
import { ZIP_INPUT_ATTRS, sanitizeZip } from '@/lib/zip-input'

interface ZipVerdictCalloutProps {
  zip: string | null
  region: string | null
  totals: ComputedTotals | null
  displayNameA: string
  displayNameB: string
  prefillSource: ZipPrefillSource
  onSubmit: (raw: string) => void
}

/**
 * Presentational ZIP verdict card for /compare/[slug]. ZIP state lives in
 * useCompareZip at the page level so sibling sections (table highlight,
 * tool links) share the same verdict; this component only owns the draft
 * input text.
 */
export function ZipVerdictCallout({
  zip,
  region,
  totals,
  displayNameA,
  displayNameB,
  prefillSource,
  onSubmit,
}: ZipVerdictCalloutProps) {
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Sync the input box when the page resolves a ZIP from the URL or saved
  // inputs (post-mount only, so the SSG-rendered empty input never mismatches).
  // Skipped while the user is typing — the async saved-inputs prefill must
  // never clobber an in-progress draft.
  useEffect(() => {
    if (zip && document.activeElement !== inputRef.current) setDraft(zip)
  }, [zip])

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault()
    onSubmit(draft)
  }

  let verdict: { headline: string; sub: string } | null = null
  if (region && totals) {
    if (totals.noClimateCompatibleWinner && totals.climateSeason) {
      verdict = {
        headline: `Neither grass fits ${region}`,
        sub: `Compare a ${totals.climateSeason.toLowerCase()}-season grass before choosing material.`,
      }
    } else if (totals.tied) {
      verdict = {
        headline: `Both grasses suit ${region}`,
        sub: 'Either grass is a defensible choice for your climate. Decide on traits, not zone fit.',
      }
    } else {
      const winnerName = totals.winnerIdx === 0 ? displayNameA : displayNameB
      const bonus = totals.zoneBonuses[totals.winnerIdx]
      verdict = {
        headline: `${winnerName} wins for ${region}`,
        sub:
          bonus === 2
            ? `${winnerName} is a primary grass for the ${region} region.`
            : `${winnerName} matches the prevailing season profile for ${region}.`,
      }
    }
  }

  // Region-specific payoff line: what lawns in this region actually fight.
  const challenges = region ? REGIONAL_CLIMATE_DATA[region]?.commonChallenges : undefined
  const challengesLine =
    verdict && challenges?.length
      ? `In your region, watch for ${challenges.map((c) => c.toLowerCase()).join(', ')}.`
      : null

  return (
    <section className="cmp-zip not-prose">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100">
            <MapPin className="h-4 w-4 text-emerald-700" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            {verdict ? (
              <>
                <p className="text-sm font-semibold text-gray-900">{verdict.headline}</p>
                <p className="mt-0.5 text-xs text-gray-600">{verdict.sub}</p>
                {challengesLine && (
                  <p className="mt-0.5 text-xs text-gray-500">{challengesLine}</p>
                )}
                {prefillSource === 'saved' && (
                  <p className="mt-1 text-[11px] text-gray-400">
                    Using your saved ZIP {zip} — change it below anytime.
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-900">Personalize for your ZIP</p>
                <p className="mt-0.5 text-xs text-gray-600">
                  Enter a 5-digit ZIP and we&apos;ll highlight which grass fits your climate zone.
                </p>
              </>
            )}
          </div>
        </div>
        <form onSubmit={submit} className="flex w-full items-center gap-2 sm:w-auto">
          <label htmlFor="compare-zip" className="sr-only">
            ZIP code
          </label>
          <input
            id="compare-zip"
            ref={inputRef}
            {...ZIP_INPUT_ATTRS}
            pattern="\d{5}"
            value={draft}
            onChange={(e) => setDraft(sanitizeZip(e.target.value))}
            placeholder="ZIP"
            className="w-24 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          />
          <button
            type="submit"
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
          >
            Check
          </button>
        </form>
      </div>
    </section>
  )
}
